import React, { useMemo, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { getTimeframeSeparation, getPeriodLabel, getSeparatorIndices } from '../../utils/timeUtils';
import { calculateSmartTicks } from '../../utils/scaleHelper';
import { isWithin1DBounds } from '../../utils/boundsHelper';

interface BackWallGridTextureProps {
  data: any[];
  timeframe: string;
  minP: number;
  maxP: number;
  getY: (price: number) => number;
  startX: number;
  width: number;
  chartWidth?: number;
  bottomOfGrid: number;
  topOfGrid: number;
  wallZ: number;
  showGrid?: boolean;
  gridColor?: string;
  gridOpacity?: number;
  gridThickness?: number;
  targetTicksX?: number;
  targetTicksY?: number;
  /** false: as linhas de preço são desenhadas ao vivo fora da textura (LiveScaleTicks). */
  drawPriceLines?: boolean;
  showPeriodSeparators?: boolean;
  periodColor?: string;
  periodOpacity?: number;
  periodLineStyle?: string;
  periodLineWidth?: number;
  showPeriodLabels?: boolean;
  extendedTimeMax?: number;
}

function hexToRgba(hex: string, alpha: number = 1): string {
  if (!hex) return `rgba(255, 255, 255, ${alpha})`;
  if (hex.startsWith('rgba')) return hex;
  if (hex.startsWith('rgb')) {
    return hex.replace('rgb', 'rgba').replace(')', `, ${alpha})`);
  }
  let c = hex.replace('#', '');
  if (c.length === 3) {
    c = c.split('').map(x => x + x).join('');
  }
  const num = parseInt(c, 16);
  if (isNaN(num)) return `rgba(255, 255, 255, ${alpha})`;
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const BackWallGridTexture: React.FC<BackWallGridTextureProps> = ({
  data,
  timeframe,
  minP,
  maxP,
  getY,
  startX,
  width,
  chartWidth,
  bottomOfGrid,
  topOfGrid,
  wallZ,
  showGrid = true,
  gridColor = '#ffffff',
  gridOpacity = 0.15,
  gridThickness = 1,
  targetTicksX = 8,
  targetTicksY = 8,
  drawPriceLines = true,
  showPeriodSeparators = true,
  periodColor = '#ffffff',
  periodOpacity = 0.3,
  periodLineStyle = 'dashed',
  periodLineWidth = 1,
  showPeriodLabels = true,
  extendedTimeMax,
}) => {
  const { gl } = useThree();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const textureRef = useRef<THREE.CanvasTexture | null>(null);
  const meshRef = useRef<THREE.Mesh>(null);

  // 1. Calculate Period Separators (Only when dataset length/timeframe changes)
  const separators = useMemo(() => {
    if (!showPeriodSeparators || !data || data.length < 2) return [];
    const seps: { x: number; label: string }[] = [];
    const sepIndices = getSeparatorIndices(data, timeframe);
    const cw = chartWidth ?? width;

    for (const sep of sepIndices) {
      const xPos = startX + data[sep.index].time * cw;
      if (isWithin1DBounds(xPos, startX, startX + cw)) {
        seps.push({
          x: xPos,
          label: sep.label,
        });
      }
    }
    return seps;
  }, [data?.length, timeframe, startX, width, chartWidth, showPeriodSeparators]);

  // Deadzone threshold for minP / maxP (0.5% buffer)
  const lastPRef = useRef<{ minP: number; maxP: number }>({ minP, maxP });
  const thresholdMinP = useRef(minP);
  const thresholdMaxP = useRef(maxP);

  const range = Math.max(maxP - minP, 0.0001);
  const minDiff = Math.abs(minP - lastPRef.current.minP) / range;
  const maxDiff = Math.abs(maxP - lastPRef.current.maxP) / range;

  // Only update price bounds if change exceeds 0.5% or on initial load
  if (minDiff > 0.005 || maxDiff > 0.005 || thresholdMinP.current === undefined) {
    lastPRef.current = { minP, maxP };
    thresholdMinP.current = minP;
    thresholdMaxP.current = maxP;
  }

  const effectiveMinP = thresholdMinP.current;
  const effectiveMaxP = thresholdMaxP.current;

  // 2. Calculate Smart Price Ticks using effective threshold bounds
  const priceTicks = useMemo(() => {
    if (!showGrid || !drawPriceLines || effectiveMinP >= effectiveMaxP) return { primary: [], secondary: [] };
    const smartTicks = calculateSmartTicks(effectiveMinP, effectiveMaxP, 'price', targetTicksY);
    return {
      primary: smartTicks.primary.filter(t => {
        const y = getY(t.value);
        return isWithin1DBounds(y, bottomOfGrid, topOfGrid);
      }),
      secondary: smartTicks.secondary.filter(t => {
        const y = getY(t.value);
        return isWithin1DBounds(y, bottomOfGrid, topOfGrid);
      })
    };
  }, [effectiveMinP, effectiveMaxP, getY, bottomOfGrid, topOfGrid, showGrid, targetTicksY, drawPriceLines]);

  // 3. Time Ticks
  const datasetTimeRange = useMemo(() => {
    if (!data || data.length < 2) return null;
    return {
      tMin: data[0].timestamp || 0,
      tMax: extendedTimeMax || data[data.length - 1].timestamp || 1,
      lastCandleTs: data[data.length - 1].timestamp || 1,
      dataRef: data
    };
  }, [data?.length, extendedTimeMax, timeframe]);

  const timeTicks = useMemo(() => {
    if (!showGrid || !datasetTimeRange) return [];
    const { tMin, tMax, lastCandleTs, dataRef } = datasetTimeRange;
    if (tMin >= tMax) return [];
    
    const cw = chartWidth ?? width;
    const smartTime = calculateSmartTicks(tMin, tMax, 'time', targetTicksX);
    
    return smartTime.primary.map(t => {
      if (t.value > lastCandleTs) {
        const timePerPixel = (lastCandleTs - tMin) / cw;
        const extraTime = t.value - lastCandleTs;
        const extraPixels = extraTime / timePerPixel;
        return startX + cw + extraPixels;
      }

      let closestIdx = 0;
      let minDiff = Infinity;
      for (let i = 0; i < dataRef.length; i++) {
        const diff = Math.abs((dataRef[i].timestamp || 0) - t.value);
        if (diff < minDiff) {
          minDiff = diff;
          closestIdx = i;
        }
      }
      return startX + dataRef[closestIdx].time * cw;
    }).filter(x => isWithin1DBounds(x, startX, startX + width));
  }, [datasetTimeRange, startX, width, chartWidth, showGrid, targetTicksX]);

  // Initialize persistent canvas and texture once
  useEffect(() => {
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
      canvasRef.current.width = 1024;
      canvasRef.current.height = 1024;
    }
    if (!textureRef.current) {
      const tex = new THREE.CanvasTexture(canvasRef.current);
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.generateMipmaps = false;
      textureRef.current = tex;
    }

    return () => {
      if (textureRef.current) {
        textureRef.current.dispose();
        textureRef.current = null;
      }
      canvasRef.current = null;
    };
  }, []);

  // Update canvas contents without allocating new textures
  useEffect(() => {
    let animationFrameId: number;
    let timeoutId: NodeJS.Timeout;

    const draw = () => {
      if (!canvasRef.current || !textureRef.current || width <= 0 || topOfGrid <= bottomOfGrid) {
        return;
      }

      const canvas = canvasRef.current;
      const canvasWidth = 1024;
      const canvasHeight = 1024;
      
      if (canvas.width !== canvasWidth || canvas.height !== canvasHeight) {
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      const gridHeight = topOfGrid - bottomOfGrid;
      const toCanvasX = (x3D: number) => ((x3D - startX) / width) * canvasWidth;
      const toCanvasY = (y3D: number) => {
        const normY = (y3D - bottomOfGrid) / gridHeight;
        return (1 - normY) * canvasHeight;
      };

      if (showGrid) {
        // Secondary Price Lines
        if (priceTicks.secondary.length > 0) {
          ctx.save();
          ctx.strokeStyle = hexToRgba(gridColor, gridOpacity * 0.45);
          ctx.lineWidth = Math.max(1, gridThickness * 0.8);
          ctx.setLineDash([2, 4]);
          ctx.beginPath();
          priceTicks.secondary.forEach(t => {
            const py = Math.floor(toCanvasY(getY(t.value))) + 0.5;
            if (py >= 0 && py <= canvasHeight) {
              ctx.moveTo(0, py);
              ctx.lineTo(canvasWidth, py);
            }
          });
          ctx.stroke();
          ctx.restore();
        }

        // Primary Price Lines
        if (priceTicks.primary.length > 0) {
          ctx.save();
          ctx.strokeStyle = hexToRgba(gridColor, gridOpacity * 0.85);
          ctx.lineWidth = Math.max(1, gridThickness);
          ctx.setLineDash([]);
          ctx.beginPath();
          priceTicks.primary.forEach(t => {
            const py = Math.floor(toCanvasY(getY(t.value))) + 0.5;
            if (py >= 0 && py <= canvasHeight) {
              ctx.moveTo(0, py);
              ctx.lineTo(canvasWidth, py);
            }
          });
          ctx.stroke();
          ctx.restore();
        }

        // Vertical Time Grid Lines
        if (timeTicks.length > 0) {
          ctx.save();
          ctx.strokeStyle = hexToRgba(gridColor, gridOpacity * 0.85);
          ctx.lineWidth = Math.max(1, gridThickness);
          ctx.setLineDash([4, 4]);
          ctx.beginPath();
          timeTicks.forEach(tx => {
            const px = Math.floor(toCanvasX(tx)) + 0.5;
            if (px >= 0 && px <= canvasWidth) {
              ctx.moveTo(px, 0);
              ctx.lineTo(px, canvasHeight);
            }
          });
          ctx.stroke();
          ctx.restore();
        }
      }

      // Period Separators
      if (showPeriodSeparators && separators.length > 0) {
        ctx.save();
        ctx.strokeStyle = hexToRgba(periodColor, periodOpacity * 0.9);
        ctx.lineWidth = Math.max(1, (periodLineWidth || 1) * 1.5);
        
        if (periodLineStyle === 'dashed') {
          ctx.setLineDash([6, 6]);
        } else if (periodLineStyle === 'dotted') {
          ctx.setLineDash([2, 4]);
        } else {
          ctx.setLineDash([]);
        }

        ctx.beginPath();
        separators.forEach((sep) => {
          const px = Math.floor(toCanvasX(sep.x)) + 0.5;
          if (px >= 0 && px <= canvasWidth) {
            ctx.moveTo(px, 0);
            ctx.lineTo(px, canvasHeight);
          }
        });
        ctx.stroke();

        if (showPeriodLabels) {
          ctx.font = '600 14px "Inter", ui-sans-serif, system-ui, sans-serif';
          ctx.textBaseline = 'top';
          
          let lastDrawnLabelPx = -999;
          const MIN_LABEL_DISTANCE_PX = 80;

          separators.forEach((sep) => {
            const px = toCanvasX(sep.x);
            if (px < 0 || px > canvasWidth) return;
            if (px - lastDrawnLabelPx < MIN_LABEL_DISTANCE_PX) return; // Prevent overlapping label collision

            lastDrawnLabelPx = px;
            ctx.fillStyle = hexToRgba(periodColor, Math.min(1.0, periodOpacity * 2.2));

            if (px < 50) {
              ctx.textAlign = 'left';
              ctx.fillText(sep.label, Math.max(10, px + 4), 12);
            } else if (px > canvasWidth - 70) {
              ctx.textAlign = 'right';
              ctx.fillText(sep.label, Math.min(canvasWidth - 10, px - 4), 12);
            } else {
              ctx.textAlign = 'center';
              ctx.fillText(sep.label, px, 12);
            }
          });
        }
        ctx.restore();
      }

      textureRef.current.needsUpdate = true;
    };

    // Throttle the draw to 1.5s (1500ms)
    timeoutId = setTimeout(() => {
      animationFrameId = requestAnimationFrame(draw);
    }, 1500);

    return () => {
      clearTimeout(timeoutId);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [
    separators,
    priceTicks,
    timeTicks,
    showGrid,
    gridColor,
    gridOpacity,
    gridThickness,
    showPeriodSeparators,
    periodColor,
    periodOpacity,
    periodLineStyle,
    periodLineWidth,
    showPeriodLabels,
    startX,
    width,
    bottomOfGrid,
    topOfGrid,
    getY
  ]);

  if (!textureRef.current) return null;

  const gridHeight = Math.max(1, topOfGrid - bottomOfGrid);
  const centerY = (bottomOfGrid + topOfGrid) / 2;

  return (
    <mesh ref={meshRef} position={[startX + width / 2, centerY, wallZ + 0.1]} renderOrder={-5}>
      <planeGeometry args={[width, gridHeight]} />
      <meshBasicMaterial 
        map={textureRef.current} 
        transparent 
        opacity={1.0}
        depthWrite={false}
        depthTest={true}
      />
    </mesh>
  );
};
